// src/hooks/useRevealOncePerSession.js
import { useEffect } from "react";

/**
 * 在同一浏览器会话中对指定页面 key 仅执行一次滚动浮现动画。
 * - 同一路由往返：不再重复动画
 * - 刷新或新开 Tab：会再次触发动画
 *
 * 使用方式：
 *   useRevealOncePerSession("home");
 *   // 在 JSX 中给需要浮现的元素添加 className="reveal [delay-*]"
 */
// 记录在当前浏览器标签页生命周期内已经展示过动画的页面 key
const revealedPageKeys = new Set();

/**
 * 解析作用域容器：支持传入 Ref、元素或选择器字符串。
 */
function resolveScope(scopeOption) {
  if (!scopeOption) return document.body;
  // React ref
  if (typeof scopeOption === "object" && scopeOption !== null && "current" in scopeOption) {
    return scopeOption.current || null;
  }
  // 选择器字符串
  if (typeof scopeOption === "string") {
    return document.querySelector(scopeOption);
  }
  // 直接传入的元素
  if (scopeOption instanceof Element) return scopeOption;
  return document.body;
}

export default function useRevealOncePerSession(pageKey, options) {
  useEffect(() => {
    if (!pageKey) return;
    const scope = options && options.scope ? options.scope : null;
    const scopeEl = resolveScope(scope);
    if (!scopeEl) return;
    let isFirstVisit = !revealedPageKeys.has(pageKey);
    let hasShownAny = false;

    const elements = [
      ...(scopeEl.classList?.contains("reveal") ? [scopeEl] : []),
      ...Array.from(scopeEl.querySelectorAll(".reveal")),
    ];

    // Helper to apply reveal immediately (no animation)
    const instantReveal = (elList) => {
      elList.forEach((el) => el.classList.add("no-animate", "show"));
    };

    if (!isFirstVisit) {
      instantReveal(elements);
    }

    // Feature-detect IntersectionObserver (older/some webviews may lack it)
    const supportsIO = typeof window !== 'undefined' && 'IntersectionObserver' in window;
    const io = supportsIO ? new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("show");
            obs.unobserve(entry.target);
            hasShownAny = true;
            // 只要有任意一个元素完成展示，即标记该页面已展示过
            if (isFirstVisit && !revealedPageKeys.has(pageKey)) {
              revealedPageKeys.add(pageKey);
              isFirstVisit = false;
            }
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -10% 0px", ...(options || {}) }
    ) : null;

    if (elements.length && isFirstVisit) {
      if (io) {
        elements.forEach((el) => io.observe(el));
      } else {
        // Fallback: if no IO support, reveal immediately
        elements.forEach((el) => el.classList.add("show"));
        revealedPageKeys.add(pageKey);
        isFirstVisit = false;
      }
    }

    // 监听后续新增的 .reveal 元素（适配异步渲染、数据加载）
    const mo = new MutationObserver((mutations) => {
      const added = [];
      mutations.forEach((m) => {
        m.addedNodes.forEach((n) => {
          if (n.nodeType !== 1) return;
          const el = /** @type {HTMLElement} */ (n);
          if (el.classList?.contains("reveal")) added.push(el);
          // 同时检查其后代
          added.push(
            ...Array.from(el.querySelectorAll?.(".reveal") || [])
          );
        });
      });
      if (!added.length) return;
      if (isFirstVisit) {
        if (io) {
          added.forEach((el) => io.observe(el));
        } else {
          instantReveal(added);
          revealedPageKeys.add(pageKey);
          isFirstVisit = false;
        }
      } else {
        instantReveal(added);
      }
    });
    mo.observe(scopeEl, { childList: true, subtree: true });

    // —— 安全兜底：若在一定时间内没有任何元素完成展示，则强制展示 ——
    let fallbackTimer = null;
    if (isFirstVisit && elements.length) {
      fallbackTimer = setTimeout(() => {
        if (!hasShownAny) {
          instantReveal(elements);
          revealedPageKeys.add(pageKey);
          isFirstVisit = false;
        }
      }, 800); // 800ms 后兜底，避免刷新首屏卡住
    }

    return () => {
      io && io.disconnect();
      mo.disconnect();
      if (fallbackTimer) clearTimeout(fallbackTimer);
    };
  }, [pageKey, options && options.scope]);
}


